import { useState } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText,
  Stack, Skeleton, Paper, alpha, useTheme, Switch, Tooltip,
  TablePagination, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Button,
} from '@mui/material';
import {
  MoreVert, Edit, Description, CardGiftcard, Block, Delete, LockOpen, VerifiedUser, Inbox, Visibility,
  AccountBalanceWallet, Notes as NotesIcon, Lock, FiberManualRecord, InfoOutlined,
} from '@mui/icons-material';
import BalanceAdjustPopover from './BalanceAdjustPopover';
import type { PartnerRow } from '../../services/partnerUserService';
import { useI18n } from '../../i18n/I18nProvider';
import { translateUiText } from '../../i18n/translateUiText';
import { formatMoney } from '../../utils/currency';
import { formatRegisteredDate, formatRegisteredTime } from '../../utils/dateFormat';

const STATUS_COLORS: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  verified: 'success',
  non_verified: 'warning',
  pending_approval: 'info',
  blocked: 'error',
  deleted: 'default',
  expired: 'error',
};

const ONBOARDING_LOCKED = new Set(['pending_approval', 'non_verified', 'expired']);

function partnerDisplayName(row: PartnerRow): string {
  const full = `${row.firstName || ''} ${row.lastName || ''}`.trim();
  return full || '—';
}

function partnerIdentityHint(row: PartnerRow): string {
  const parts = [`ID #${String(row._id).slice(-6).toUpperCase()}`];
  if (row.referralCode) parts.push(`Code ${row.referralCode}`);
  return parts.join(' · ');
}

function partnerPhone(row: PartnerRow): string {
  const raw = String(row.phone || '').trim();
  if (!raw) return '';
  if (raw.startsWith('+')) return raw;
  const cc = String((row.metadata as { phoneCountryCode?: string } | undefined)?.phoneCountryCode || '').trim();
  return cc ? `${cc.startsWith('+') ? cc : `+${cc}`} ${raw}` : raw;
}

function timeAgo(ts?: string | null): string {
  if (!ts) return '';
  const min = Math.max(0, Math.round((Date.now() - new Date(ts).getTime()) / 60_000));
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  if (min < 1440) return `${Math.floor(min / 60)} h ago`;
  return `${Math.floor(min / 1440)} d ago`;
}

const LOCKED_STATUS_REASON: Record<string, string> = {
  pending_approval: 'Locked — documents/onboarding awaiting approval',
  non_verified: 'Locked — provider is not verified yet',
  expired: 'Locked — a required document has expired',
};

function partnerNotesText(row: PartnerRow): string {
  const meta = (row.metadata || {}) as Record<string, unknown>;
  const adminNotes = String(meta.adminNotes || meta.notes || '').trim();
  const blocked = row.isBlocked ? String(row.blockedReason || '').trim() : '';
  const parts = [
    adminNotes ? `Admin notes:\n${adminNotes}` : '',
    blocked ? `Blocked reason:\n${blocked || 'No reason recorded'}` : '',
  ].filter(Boolean);
  return parts.join('\n\n');
}

interface PartnerListTableProps {
  rows: PartnerRow[];
  loading?: boolean;
  walletLoading?: boolean;
  onEdit: (row: PartnerRow) => void;
  onDocuments: (row: PartnerRow) => void;
  onReferrals: (row: PartnerRow) => void;
  onProfile?: (row: PartnerRow) => void;
  onView?: (row: PartnerRow) => void;
  onBlock: (row: PartnerRow) => void;
  onUnblock: (row: PartnerRow) => void;
  onDelete: (row: PartnerRow) => void;
  onToggleActive: (row: PartnerRow, isActive: boolean) => void;
  onWalletAdjust: (row: PartnerRow, mode: 'credit' | 'debit', amount: number, note: string) => unknown | Promise<unknown>;
  onWalletView?: (row: PartnerRow) => void;
  onRefresh: () => void;
  totalCount?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}

export default function PartnerListTable({
  rows,
  loading,
  walletLoading,
  onEdit,
  onDocuments,
  onReferrals,
  onProfile,
  onView,
  onBlock,
  onUnblock,
  onDelete,
  onToggleActive,
  onWalletAdjust,
  onWalletView,
  onRefresh,
  totalCount,
  page = 0,
  pageSize = 25,
  onPageChange,
  onPageSizeChange,
}: PartnerListTableProps) {
  const theme = useTheme();
  const { t } = useI18n();
  const tl = (s: string) => translateUiText(s, t);
  const serialOffset = page * pageSize;
  const showPagination = totalCount != null && onPageChange && onPageSizeChange;
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<PartnerRow | null>(null);
  const [notesRow, setNotesRow] = useState<PartnerRow | null>(null);
  const [toggleConfirm, setToggleConfirm] = useState<{ row: PartnerRow; next: boolean } | null>(null);

  const openMenu = (e: React.MouseEvent<HTMLElement>, row: PartnerRow) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuRow(row);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  if (loading) {
    return (
      <Paper elevation={0} sx={{ borderRadius: 2.5, border: 1, borderColor: 'divider' }}>
        {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} height={52} sx={{ mx: 2, my: 0.5 }} />)}
      </Paper>
    );
  }

  if (!rows.length) {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2.5,
          border: 1,
          borderColor: 'divider',
          borderStyle: 'dashed',
          py: 6,
          textAlign: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Inbox sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography variant="subtitle1" fontWeight={700} color="text.secondary">No partners found</Typography>
        <Typography variant="body2" color="text.disabled">Try adjusting filters or add a new partner</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ borderRadius: 2.5, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="flex-start"
        sx={{ px: 2, py: 1, bgcolor: alpha(theme.palette.info.main, 0.06), borderBottom: 1, borderColor: 'divider' }}
      >
        <InfoOutlined sx={{ fontSize: 16, color: 'info.main', mt: 0.25 }} />
        <Typography variant="caption" color="text.secondary">
          Only <strong>Verified</strong> partners can be switched to <strong>Available for jobs</strong>. Pending Approval,
          Non Verified and Expired partners are locked until approved; Blocked and Deleted partners cannot be dispatched.
          Available partners receive jobs when they are online in the app.
        </Typography>
      </Stack>
      <TableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', width: 56 }}>{tl('S.No')}</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>{tl('Partner')}</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', display: { xs: 'none', sm: 'table-cell' } }}>{tl('Contact')}</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', display: { xs: 'none', md: 'table-cell' } }}>{tl('Zone')}</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', display: { xs: 'none', lg: 'table-cell' } }}>{tl('Registered')}</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>{tl('Status')}</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', display: { xs: 'none', lg: 'table-cell' } }}>{tl('Wallet')}</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', width: 96 }} align="right">{tl('Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const name = partnerDisplayName(row);
              const isDeleted = row.partnerStatus === 'deleted';
              const isBlocked = row.isBlocked;
              const statusKey = String(row.partnerStatus || 'unknown');
              const availabilityLocked = ONBOARDING_LOCKED.has(statusKey);
              const notes = partnerNotesText(row);
              const phone = partnerPhone(row);
              const email = String(row.email || '').trim();
              const profile = row.providerProfile;

              return (
                <TableRow
                  key={row._id}
                  hover
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) } }}
                  onClick={() => (onView ? onView(row) : onEdit(row))}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} color="text.secondary">{serialOffset + index + 1}</Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Typography variant="body2" fontWeight={700}>{name}</Typography>
                      {row.isVerified && <VerifiedUser color="success" sx={{ fontSize: 16 }} />}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {partnerIdentityHint(row)}
                    </Typography>
                    {phone && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ display: { sm: 'none' } }}>
                        {phone}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }} onClick={(e) => e.stopPropagation()}>
                    {phone ? (
                      <Typography
                        variant="body2" fontWeight={600} noWrap
                        component="a" href={`tel:${phone.replace(/\s/g, '')}`}
                        sx={{ color: 'text.primary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {phone}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.disabled">No phone on file</Typography>
                    )}
                    {email ? (
                      <Typography
                        variant="caption" display="block" noWrap title={email}
                        component="a" href={`mailto:${email}`}
                        sx={{ color: 'text.secondary', textDecoration: 'none', maxWidth: 220, '&:hover': { textDecoration: 'underline' } }}
                      >
                        {email}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.disabled" display="block">No email on file</Typography>
                    )}
                    {profile && (
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                        <FiberManualRecord sx={{ fontSize: 9, color: profile.isOnline ? 'success.main' : 'text.disabled' }} />
                        <Typography variant="caption" color="text.secondary">
                          {profile.isOnline ? 'Online in app' : 'Offline in app'}
                        </Typography>
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Typography variant="caption" fontWeight={600} display="block">
                      {row.zoneId?.name || 'No zone assigned'}
                    </Typography>
                    {row.assignedZoneIsWorld && (
                      <Tooltip title="World Zone is the catch-all default. Assign the provider's operating zone via Edit.">
                        <Typography variant="caption" color="warning.main" display="block">Default zone — not specific</Typography>
                      </Tooltip>
                    )}
                    {profile && (
                      profile.location ? (
                        row.liveZone ? (
                          <Typography
                            variant="caption" display="block"
                            color={row.zoneMatchesLocation === false ? 'warning.main' : 'text.secondary'}
                          >
                            {row.zoneMatchesLocation === false ? 'Currently in ' : 'Live: '}
                            {row.liveZone.name}
                            {profile.locationUpdatedAt ? ` · ${timeAgo(profile.locationUpdatedAt)}` : ''}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="warning.main" display="block">Live location outside all zones</Typography>
                        )
                      ) : (
                        <Typography variant="caption" color="text.disabled" display="block">No live location yet</Typography>
                      )
                    )}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    <Typography variant="caption" display="block" fontWeight={600}>
                      {formatRegisteredDate(row.createdAt)}
                    </Typography>
                    {row.createdAt && (
                      <Typography variant="caption" color="text.secondary">
                        {formatRegisteredTime(row.createdAt)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {isDeleted || isBlocked ? (
                      <Stack spacing={0.35}>
                        <Chip size="small" label={isBlocked ? 'Blocked' : 'Deleted'} color="error" variant="outlined" />
                      </Stack>
                    ) : availabilityLocked ? (
                      <Stack spacing={0.35}>
                        <Chip
                          size="small"
                          label={statusKey.replace(/_/g, ' ')}
                          color={STATUS_COLORS[statusKey] || 'default'}
                          variant="outlined"
                          sx={{ textTransform: 'capitalize', alignSelf: 'flex-start' }}
                        />
                        <Tooltip title="Availability can be turned on only after the provider is approved (Verified).">
                          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ opacity: 0.7 }}>
                            <Lock sx={{ fontSize: 13, color: 'text.disabled' }} />
                            <Typography variant="caption" color="text.secondary">Not available</Typography>
                            <Switch size="small" checked={false} disabled inputProps={{ 'aria-label': 'Availability locked until approval' }} />
                          </Stack>
                        </Tooltip>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35, maxWidth: 170 }}>
                          {LOCKED_STATUS_REASON[statusKey] || 'Locked until approval is completed'}
                        </Typography>
                      </Stack>
                    ) : (
                      <Stack spacing={0.35}>
                        <Chip
                          size="small"
                          label={statusKey.replace(/_/g, ' ')}
                          color={STATUS_COLORS[statusKey] || 'default'}
                          variant="outlined"
                          sx={{ textTransform: 'capitalize', alignSelf: 'flex-start' }}
                        />
                        <Tooltip
                          title={row.isActive
                            ? 'Approved and available — receives bookings/dispatch when online in the app. Switch off to pause.'
                            : 'Approved but paused — will not receive bookings/dispatch until switched on.'}
                        >
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="caption" color={row.isActive ? 'success.main' : 'text.secondary'} fontWeight={600}>
                              {row.isActive ? 'Available for jobs' : 'Paused'}
                            </Typography>
                            <Switch
                              size="small"
                              color="success"
                              checked={!!row.isActive}
                              onChange={(e) => setToggleConfirm({ row, next: e.target.checked })}
                              inputProps={{ 'aria-label': 'Available for jobs' }}
                            />
                          </Stack>
                        </Tooltip>
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }} onClick={(e) => e.stopPropagation()}>
                    <Stack spacing={0.5}>
                      <BalanceAdjustPopover
                        type="wallet"
                        displayValue={`${formatMoney((row.walletBalance ?? 0))}`}
                        currentBalance={row.walletBalance ?? 0}
                        disabled={isDeleted}
                        disabledReason={isDeleted ? 'Deleted partner' : undefined}
                        loading={walletLoading}
                        onRefresh={onRefresh}
                        onApply={(mode, amount, note) => onWalletAdjust(row, mode, amount, note)}
                        lastTxn={row.lastWalletTxn}
                        onViewHistory={onWalletView ? () => onWalletView(row) : undefined}
                      />
                      {row.lastWalletTxn ? (
                        <Tooltip
                          title={`${row.lastWalletTxn.description || row.lastWalletTxn.type}${row.lastWalletTxn.adjustedByName ? ` — by ${row.lastWalletTxn.adjustedByName}` : ''}`}
                        >
                          <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ maxWidth: 170 }}>
                            Last: {row.lastWalletTxn.amount >= 0 ? '+' : '−'}{formatMoney(Math.abs(row.lastWalletTxn.amount))}
                            {' · '}{formatRegisteredDate(row.lastWalletTxn.createdAt)}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.disabled" display="block">No transactions yet</Typography>
                      )}
                      {onWalletView && !isDeleted && (
                        <Typography
                          variant="caption"
                          color="primary"
                          sx={{ cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                          onClick={() => onWalletView(row)}
                        >
                          View transactions{row.walletTxnCount ? ` (${row.walletTxnCount})` : ''}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" spacing={0.25} justifyContent="flex-end" alignItems="center">
                      <Tooltip title={notes ? 'View notes' : 'No notes yet — open to view'}>
                        <IconButton
                          size="small"
                          color={notes ? 'primary' : 'default'}
                          onClick={() => setNotesRow(row)}
                          aria-label="View partner notes"
                        >
                          <NotesIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {onView && (
                        <Tooltip title="View">
                          <IconButton size="small" color="primary" onClick={() => onView(row)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Actions">
                        <IconButton size="small" onClick={(e) => openMenu(e, row)}>
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {showPagination && (
        <>
          <Divider />
          <TablePagination
            component="div"
            count={totalCount!}
            page={page}
            onPageChange={(_, p) => onPageChange!(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => onPageSizeChange!(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </>
      )}

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        {onView && (
          <MenuItem onClick={() => { menuRow && onView(menuRow); closeMenu(); }}>
            <ListItemIcon><Visibility fontSize="small" /></ListItemIcon>
            <ListItemText>View</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => { menuRow && setNotesRow(menuRow); closeMenu(); }}>
          <ListItemIcon><NotesIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Notes</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { menuRow && onEdit(menuRow); closeMenu(); }}>
          <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { menuRow && onDocuments(menuRow); closeMenu(); }}>
          <ListItemIcon><Description fontSize="small" /></ListItemIcon>
          <ListItemText>Documents{menuRow?.pendingDocCount ? ` (${menuRow.pendingDocCount})` : ''}</ListItemText>
        </MenuItem>
        {onProfile && (
          <MenuItem onClick={() => { menuRow && onProfile(menuRow); closeMenu(); }}>
            <ListItemIcon><VerifiedUser fontSize="small" /></ListItemIcon>
            <ListItemText>Provider Profile</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => { menuRow && onReferrals(menuRow); closeMenu(); }}>
          <ListItemIcon><CardGiftcard fontSize="small" /></ListItemIcon>
          <ListItemText>Referrals ({menuRow?.referralCount ?? 0})</ListItemText>
        </MenuItem>
        {onWalletView && (
          <MenuItem onClick={() => { menuRow && onWalletView(menuRow); closeMenu(); }}>
            <ListItemIcon><AccountBalanceWallet fontSize="small" /></ListItemIcon>
            <ListItemText>Wallet transactions</ListItemText>
          </MenuItem>
        )}
        {menuRow?.isBlocked ? (
          <MenuItem onClick={() => { menuRow && onUnblock(menuRow); closeMenu(); }}>
            <ListItemIcon><LockOpen fontSize="small" /></ListItemIcon>
            <ListItemText>Unblock</ListItemText>
          </MenuItem>
        ) : menuRow?.partnerStatus !== 'deleted' && (
          <MenuItem onClick={() => { menuRow && onBlock(menuRow); closeMenu(); }}>
            <ListItemIcon><Block fontSize="small" /></ListItemIcon>
            <ListItemText>Block</ListItemText>
          </MenuItem>
        )}
        {menuRow?.partnerStatus !== 'deleted' && (
          <MenuItem onClick={() => { menuRow && onDelete(menuRow); closeMenu(); }} sx={{ color: 'error.main' }}>
            <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <Dialog open={!!toggleConfirm} onClose={() => setToggleConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {toggleConfirm?.next ? 'Make partner available for jobs?' : 'Pause partner availability?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {toggleConfirm?.next
              ? `${toggleConfirm ? partnerDisplayName(toggleConfirm.row) : ''} will start receiving bookings / dispatch when online in the app.`
              : `${toggleConfirm ? partnerDisplayName(toggleConfirm.row) : ''} will stop receiving new bookings / dispatch. Jobs already assigned are not affected.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToggleConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={toggleConfirm?.next ? 'success' : 'warning'}
            onClick={() => {
              if (toggleConfirm) onToggleActive(toggleConfirm.row, toggleConfirm.next);
              setToggleConfirm(null);
            }}
          >
            {toggleConfirm?.next ? 'Make available' : 'Pause'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!notesRow} onClose={() => setNotesRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Notes — {notesRow ? partnerDisplayName(notesRow) : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color={notesRow && partnerNotesText(notesRow) ? 'text.primary' : 'text.secondary'} sx={{ whiteSpace: 'pre-wrap' }}>
            {(notesRow && partnerNotesText(notesRow)) || 'No notes recorded for this partner.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
